import type { Opportunity } from '@/lib/opportunities'
import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from './baseQuery'
import type { DeliverableItem } from '@adthlete/shared-frontend'
import type { Catalogue, LessonProgress, Progress, Track } from '@adthlete/common/utils/academy'

export type { DeliverableItem } from '@adthlete/shared-frontend'

export interface CollaborationEvent {
  id: string
  title: string
  description: string | null
  location: string | null
  startAt: string
  endAt: string | null
  status: 'scheduled' | 'cancelled'
  createdAt: string
  linkId?: string
  rsvpStatus: 'pending' | 'accepted' | 'declined' | 'proposed_changes'
  athleteNote: string | null
  proposedStartAt: string | null
  proposedEndAt: string | null
  rsvpRespondedAt: string | null
}

export interface Brand {
  id?: string
  name: string
  logo: string
}

export interface MilestoneCheckIn {
  active: boolean
  response: 'on_track' | 'needs_more_time' | null
  responseAt: string | null
}

export interface MilestoneDateProposal {
  id: string
  proposedDueDate: string
  reason: string | null
  status: 'open' | 'accepted' | 'declined' | 'cancelled' | 'expired'
  responseNote: string | null
  createdAt: string
  respondedAt: string | null
  expiresAt: string
}

export interface Milestone {
  id: number
  name: string
  slug?: string
  description: string
  sequenceOrder: number
  dueDate: string
  status: string // 'pending', 'submitted', 'approved'
  requiresApproval: boolean
  approvedSubmissionId?: number
  metadata?: any
  completedAt?: string
  createdAt: string
  updatedAt: string
  submissions?: any[]
  checkIn?: MilestoneCheckIn | null
  dateProposal?: MilestoneDateProposal | null
}

/** One of the brand's review notes on a submission: a video moment, a caption edit, or general */
export interface SubmissionNote {
  id: string
  text: string
  target: 'video' | 'caption' | 'general'
  deliverableId: string | null
  timestampSec: number | null
  suggestion: { from: string; to: string } | null
  isResolved: boolean
  resolvedAt: string | null
  createdAt: string
  author: {
    id: string
    name: string | null
    avatar: string | null
    side: 'athlete' | 'brand'
  } | null
  replies: SubmissionNote[]
}

/** An organic post on the content calendar */
export interface AcademyState {
  /** The published lessons, their tracks and the stage names */
  catalogue: Catalogue
  /** Every stage and track, sent while nothing is published yet */
  outline?: { stages: Record<number, string>; tracks: Track[] }
  progress: Progress
  /** Lessons watched through, and the score points they're worth */
  watched: number
  points: number
}

/** The athlete's strategy call with the Creator Lab */
export interface LabCall {
  id: string
  startsAt: string
  minutes: number
  status: 'booked' | 'done' | 'missed' | 'cancelled'
  phone: string | null
}
export interface LabCallState {
  /** Their school has the Creator Lab */
  lab: boolean
  call: LabCall | null
  /** Open call times, ISO; the Lab takes calls around lunch and 4-5 pm Eastern */
  times: string[]
  timezone: string
}

export interface PlannedPost {
  id: string
  /** Where the piece is: an idea (no date yet), planned, filmed, edited, posted */
  stage: 'idea' | 'planned' | 'filmed' | 'edited' | 'posted'
  /** Null for an idea in the idea bank */
  plannedOn: string | null
  plannedTime: string | null
  platform: 'instagram' | 'tiktok' | 'youtube'
  theme: { id: string | null; label: string } | null
  idea: string | null
  caption: string | null
  /** 'suggested' is a manager's suggestion waiting on you (see getPostSuggestions) */
  status: 'idea' | 'planned' | 'posted' | 'skipped' | 'suggested'
  postedOn: string | null
  postUrl: string | null
  plannedBy: { role: 'athlete' | 'manager' | 'creator_lab'; name: string | null }
  createdAt: string
  // The idea and how it gets made
  hook: string | null
  concept: string | null
  whyNow: string | null
  shotList: { text: string; done: boolean }[]
  location: string | null
  wardrobe: string | null
  filmedBy: string | null
  shootOn: string | null
  shootTime: string | null
  editOn: string | null
  shotAt: string | null
  editedAt: string | null
  source: 'manual' | 'assistant' | 'trend' | 'manager' | 'creator_lab'
  trend: { id: string; title: string | null } | null
  suggested: boolean
  suggestionNote: string | null
}

/** What the content assistant proposes: a full piece, ready to add to the calendar */
export interface ProposedPiece {
  title: string
  themeId: string | null
  themeLabel: string
  platform: 'instagram' | 'tiktok' | 'youtube'
  postOn: string
  postTime: string | null
  hook: string
  concept: string
  whyNow: string | null
  shotList: string[]
  location: string | null
  wardrobe: string | null
  caption: string | null
  shootOn: string
  editOn: string
  trendTitle: string | null
}

export interface ContentTrend {
  id: string
  platform: string
  kind: 'hashtag' | 'sound' | 'format'
  title: string
  description: string | null
  howToUse: string | null
  url: string | null
  rank: number | null
  weekOf: string
}

export type AssistantMessage = { role: 'user' | 'assistant'; content: string }

export interface PlannerWeek {
  weekStart: string
  posts: PlannedPost[]
  summary: {
    goal: number
    thisWeek: number
    weekStart: string
    weeks: { weekStart: string; posted: number }[]
  }
  themes: { id: string; label: string }[]
}

export interface Collaboration {
  id: string
  /** The athlete archived this deal: it sits under Archived on the Deals page and off Home */
  archived?: boolean
  archivedAt?: string | null
  /** NIL Go clearance, computed by the server the same way the brand sees it */
  compliance?: import('@adthlete/common/utils/nilCompliance').NilCompliance
  booking?: any
  waveId?: string
  title: string
  platform: string[]
  paymentAmount: number
  paymentType?: 'fixed' | 'custom'
  customPaymentTerms?: string | null
  status: string // 'offered', 'accepted', 'rejected', etc.
  dealType?: 'content' | 'gifting' | 'appearance' | 'licensing'
  licensingEnabled?: boolean
  offerDetails?: any
  /** Gifting terms, bonuses unlocked by views, and the paid-usage window */
  performance?: any
  waveStatus?: string
  brief: string
  instructions?: string
  script?: string | null
  audioUrl?: string | null
  imageUrl?: string | null
  postLinks?: string[]
  contentCreatedAt?: string | null
  contentApprovedAt?: string | null
  contentApprovedUrl?: string | null
  invitationLink?: string | null
  brand: Brand
  brandId?: string
  bidReceivedAt?: string
  dueDate?: string
  startDate?: string
  endDate?: string
  contentDueDate?: string
  metrics?: any
  paymentStatus?: string
  paymentDate?: string | null
  notificationShown?: boolean
  dueDateNotificationShown?: boolean
  complianceNeeded?: boolean
  conceptStatus?: string // 'submitted', 'approved', 'changes_requested'
  videoReviewStatus?: string // 'submitted', 'approved', 'changes_requested'
  finalVideoStatus?: string // 'pending', 'posted'
  submittedAt?: string
  approvedAt?: string
  milestones?: Milestone[]
  organization?: {
    name: string
    logo: string
  }
  brandManager?: {
    name: string
    email: string
    avatar: string
  }
  athlete?: {
    name: string
    email: string
    avatar: string
  }
  legacyContentSubmission?: any
  structuredDeliverables?: DeliverableItem[] | null
  // Compliance fields
  dealExecutedAt?: string | null
  payout?: number | null
  contractUrl?: string | null
  nilGoStatus?: string
  nilGoConfirmationNumber?: string | null
  nilGoSubmittedAt?: string | null
  nilGoFields?: Record<string, string | null> | null
  institutionalDisclosureStatus?: string
  institutionalDisclosureSubmittedAt?: string | null
  contractScanResult?: {
    overallResult: 'clean' | 'flagged' | 'not_scanned'
    scannedAt?: string | null
    flags?: Array<{
      id: string
      category: string
      severity: 'critical' | 'warning' | 'info'
      excerpt: string
      rule: string
      recommendation: string
    }>
  } | null
  timeline?: Array<{
    id: number
    body: string
    messageType: string
    createdAt: string
    metadata?: Record<string, unknown>
    milestone?: {
      id: number
      name: string
    } | null
    author?: {
      id: string
      name: string
      avatar: string | null
    } | null
  }>
}

export const collaborationsApi = createApi({
  reducerPath: 'campaignsApi',
  baseQuery,
  tagTypes: [
    'Collaboration',
    'CollaborationDetail',
    'Opportunity',
    'Notes',
    'PlannedPosts',
    'Academy',
    'LabCall',
  ],
  endpoints: (builder) => ({
    // The athlete's own Marketplace page, as brands see it
    getMyMarketplaceProfile: builder.query<
      import('@adthlete/shared-frontend').MarketplaceProfileData,
      void
    >({
      query: () => '/athlete/marketplace-profile',
      keepUnusedDataFor: 0,
    }),
    getCampaigns: builder.query<Collaboration[], void>({
      query: () => '/athlete/collaborations',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Collaboration' as const, id })),
              { type: 'Collaboration', id: 'LIST' },
            ]
          : [{ type: 'Collaboration', id: 'LIST' }],
    }),
    getCampaignDetail: builder.query<Collaboration, string>({
      query: (campaignId) => `/athlete/collaborations/${campaignId}`,
      providesTags: (result, error, id) => [{ type: 'CollaborationDetail', id }],
    }),
    // The Marketplace: opportunities brands opened to applications
    getOpportunities: builder.query<Opportunity[], void>({
      query: () => '/athlete/opportunities',
      transformResponse: (res: { opportunities: Opportunity[] }) => res.opportunities ?? [],
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Opportunity' as const, id })),
              { type: 'Opportunity', id: 'LIST' },
            ]
          : [{ type: 'Opportunity', id: 'LIST' }],
    }),
    getOpportunity: builder.query<Opportunity, string>({
      query: (id) => `/athlete/opportunities/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Opportunity', id }],
    }),
    applyToOpportunity: builder.mutation<
      { dealId: string; status: string },
      { id: string; message: string; ask?: number | null }
    >({
      query: ({ id, message, ask }) => ({
        url: `/athlete/opportunities/${id}/apply`,
        method: 'POST',
        body: { message, ask },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Opportunity', id },
        { type: 'Opportunity', id: 'LIST' },
        { type: 'Collaboration', id: 'LIST' },
      ],
    }),
    acceptCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: { status: 'accepted' },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Collaboration', id },
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id },
      ],
    }),
    rejectCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: { status: 'rejected' },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Collaboration', id },
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id },
      ],
    }),
    restoreCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: { status: 'offered' },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Collaboration', id },
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id },
      ],
    }),
    updateMilestoneMetadata: builder.mutation<
      { campaignId: string; milestoneId: number; metadata: any },
      { campaignId: string; milestoneId: number; metadata: any }
    >({
      query: ({ campaignId, milestoneId, metadata }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/metadata`,
        method: 'PATCH',
        body: { metadata },
      }),
      invalidatesTags: ['CollaborationDetail'],
    }),
    submitMilestone: builder.mutation<
      any,
      { campaignId: string; milestoneId: number; submissionData: any }
    >({
      query: ({ campaignId, milestoneId, submissionData }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions`,
        method: 'POST',
        body: submissionData,
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'Collaboration', id: 'LIST' },
        { type: 'Collaboration', id: campaignId },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    deleteMilestoneSubmission: builder.mutation<
      any,
      { campaignId: string; milestoneId: number; submissionId: number }
    >({
      query: ({ campaignId, milestoneId, submissionId }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions/${submissionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CollaborationDetail'],
    }),
    submitMilestoneCheckin: builder.mutation<
      { id: number; milestoneId: string; response: string; responseAt: string },
      { campaignId: string; milestoneId: string; response: 'on_track' | 'needs_more_time' }
    >({
      query: ({ campaignId, milestoneId, response }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/checkin`,
        method: 'POST',
        body: { response },
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    createMilestoneDateProposal: builder.mutation<
      MilestoneDateProposal,
      {
        campaignId: string
        milestoneId: string
        proposedDueDate: string
        reason?: string | null
      }
    >({
      query: ({ campaignId, milestoneId, proposedDueDate, reason }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/date-proposals`,
        method: 'POST',
        body: { proposedDueDate, reason: reason ?? null },
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    cancelMilestoneDateProposal: builder.mutation<
      MilestoneDateProposal,
      { campaignId: string; milestoneId: string; proposalId: string }
    >({
      query: ({ campaignId, milestoneId, proposalId }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/date-proposals/${proposalId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getSubmissionTimeline: builder.query<
      any,
      { campaignId: string; milestoneId: number; submissionId: number }
    >({
      query: ({ campaignId, milestoneId, submissionId }) =>
        `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions/${submissionId}/timeline`,
      providesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    addSubmissionComment: builder.mutation<
      any,
      {
        campaignId: string
        milestoneId: number
        submissionId: number
        text: string
        commentType?: string
        parentCommentId?: number
        metadata?: any
      }
    >({
      query: ({
        campaignId,
        milestoneId,
        submissionId,
        text,
        commentType,
        parentCommentId,
        metadata,
      }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions/${submissionId}/comments`,
        method: 'POST',
        body: { text, commentType, parentCommentId, metadata },
      }),
      // Invalidate the specific timeline query if possible, or just CollaborationDetail to be safe/lazy
      // Ideally we would have a 'Timeline' tag
      invalidatesTags: ['CollaborationDetail'],
    }),
    createStandaloneCollaboration: builder.mutation<Collaboration, any>({
      query: (data) => ({
        url: '/athlete/collaborations/standalone',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Collaboration', id: 'LIST' }],
    }),
    inviteBrand: builder.mutation<
      void,
      { campaignId: string; email: string; brandName?: string; message?: string }
    >({
      query: ({ campaignId, email, brandName, message }) => ({
        url: `/athlete/collaborations/${campaignId}/invite`,
        method: 'POST',
        body: { email, brandName, message },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'Collaboration', id: campaignId },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getPendingInvitations: builder.query<any[], void>({
      query: () => '/athlete/collaborations/invitations/pending',
      providesTags: ['Collaboration'],
    }),
    getCreativeIdeas: builder.query<
      {
        ideas: Array<{
          id: string
          title: string
          content: {
            concept?: string
            theme?: string
            integration_concept?: string
            talking_points?: string
          }
          createdBy: string
          createdByTeam?: string | null
          createdAt: string
        }>
      },
      string
    >({
      query: (campaignId) => `/athlete/collaborations/${campaignId}/creative-ideas`,
    }),
    sendCaptionChat: builder.mutation<
      { reply: string; suggestedCaption: string | null },
      { campaignId: string; messages: Array<{ role: 'user' | 'assistant'; content: string }> }
    >({
      query: ({ campaignId, messages }) => ({
        url: `/athlete/collaborations/${campaignId}/caption-chat`,
        method: 'POST',
        body: { messages },
      }),
    }),
    getVideoSuggestions: builder.query<
      {
        suggestions: Array<{
          id: string
          type: string
          videoUrl?: string
          filename?: string
          fileSize?: number
          contentType?: string
          caption?: string
          deliverableId?: string
          sameAsDeliverableId?: string
          createdAt: string
        }>
      },
      string
    >({
      query: (campaignId) => `/athlete/collaborations/${campaignId}/video-suggestions`,
    }),
    sendCollaborationMessage: builder.mutation<any, { campaignId: string; message: string }>({
      query: ({ campaignId, message }) => ({
        url: `/athlete/collaborations/${campaignId}/messages`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    // Archive / unarchive a deal in the athlete's own lists. The brand and manager see no change.
    setCollaborationArchived: builder.mutation<
      { id: string; archived: boolean },
      { id: string; archived: boolean }
    >({
      query: ({ id, archived }) => ({
        url: `/athlete/collaborations/${id}/archive`,
        method: archived ? 'POST' : 'DELETE',
      }),
      invalidatesTags: [{ type: 'Collaboration', id: 'LIST' }],
    }),
    updateCollaboration: builder.mutation<
      void,
      {
        campaignId: string
        updates: {
          nilGoStatus?: string
          nilGoConfirmationNumber?: string | null
          nilGoSubmittedAt?: string | null
          nilGoFields?: Record<string, string | null> | null
          institutionalDisclosureStatus?: string
          institutionalDisclosureSubmittedAt?: string | null
          /** The links to the live post; completes the go-live step */
          livePostLinks?: string[]
        }
      }
    >({
      query: ({ campaignId, updates }) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    updateDeliverables: builder.mutation<
      { structuredDeliverables: import('@adthlete/shared-frontend').DeliverableItem[] },
      { campaignId: string; deliverables: import('@adthlete/shared-frontend').DeliverableItem[] }
    >({
      query: ({ campaignId, deliverables }) => ({
        url: `/athlete/collaborations/${campaignId}/deliverables`,
        method: 'PATCH',
        body: { deliverables },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    scanContract: builder.mutation<
      {
        overallResult: 'clean' | 'flagged' | 'not_scanned'
        contractAvailable: boolean
        scannedAt?: string
        flags?: any[]
      },
      string
    >({
      query: (campaignId) => ({
        url: `/athlete/campaigns/${campaignId}/compliance/scan`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, campaignId) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getCalendarEvents: builder.query<
      { connected: boolean; events: any[] },
      { start: string; end: string }
    >({
      query: ({ start, end }) =>
        `/athlete/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    }),
    // Creator Academy progress, recorded as the athlete watches
    getAcademyProgress: builder.query<AcademyState, void>({
      query: () => '/athlete/academy',
      providesTags: ['Academy'],
    }),
    reportAcademyEvent: builder.mutation<
      // points come back only on the report that finished a lesson (the score is costly)
      Omit<AcademyState, 'points'> & {
        points?: number
        lessonId: string
        lesson: LessonProgress
        earned: boolean
      },
      {
        lessonId: string
        event: 'play' | 'progress' | 'pause' | 'ended' | 'done'
        position: number
        duration?: number
      }
    >({
      query: (body) => ({ url: '/athlete/academy/events', method: 'POST', body }),
      // Keep the cached progress current without refetching the whole list
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            collaborationsApi.util.updateQueryData('getAcademyProgress', undefined, (draft) => {
              draft.progress[data.lessonId] = data.lesson
              draft.watched = data.watched
              if (data.points !== undefined) draft.points = data.points
            }),
          )
        } catch {
          // The next report or a reload catches up
        }
      },
    }),
    getMilestoneEvents: builder.query<{ events: any[] }, { start: string; end: string }>({
      query: ({ start, end }) =>
        `/athlete/calendar/milestone-events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    }),
    getCollaborationEvents: builder.query<CollaborationEvent[], string>({
      query: (campaignId) => `/athlete/collaborations/${campaignId}/events`,
      providesTags: (result, error, campaignId) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    rsvpCollaborationEvent: builder.mutation<
      CollaborationEvent,
      {
        campaignId: string
        eventId: string
        rsvpStatus: 'accepted' | 'declined' | 'proposed_changes'
        athleteNote?: string
        proposedStartAt?: string
        proposedEndAt?: string
      }
    >({
      query: ({
        campaignId,
        eventId,
        rsvpStatus,
        athleteNote,
        proposedStartAt,
        proposedEndAt,
      }) => ({
        url: `/athlete/collaborations/${campaignId}/events/${eventId}/rsvp`,
        method: 'PATCH',
        body: { rsvpStatus, athleteNote, proposedStartAt, proposedEndAt },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getCollaborationCalendarEvents: builder.query<
      { events: any[] },
      { start: string; end: string }
    >({
      query: ({ start, end }) =>
        `/athlete/calendar/collaboration-events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    }),
    // ── The deal screen's own actions ─────────────────────────────────────────
    counterOffer: builder.mutation<any, { campaignId: string; amount: number; note?: string }>({
      query: ({ campaignId, amount, note }) => ({
        url: `/athlete/collaborations/${campaignId}/counter`,
        method: 'POST',
        body: { amount, note },
      }),
      invalidatesTags: (_r, _e, { campaignId }) => [
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    quoteRate: builder.mutation<any, { campaignId: string; amount: number; note?: string }>({
      query: ({ campaignId, amount, note }) => ({
        url: `/athlete/collaborations/${campaignId}/quote`,
        method: 'POST',
        body: { amount, note },
      }),
      invalidatesTags: (_r, _e, { campaignId }) => [
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    completeStep: builder.mutation<
      any,
      { campaignId: string; milestoneId: string; body?: Record<string, unknown> }
    >({
      query: ({ campaignId, milestoneId, body }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/complete`,
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: (_r, _e, { campaignId }) => [
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    approveLicensing: builder.mutation<any, { campaignId: string }>({
      query: ({ campaignId }) => ({
        url: `/athlete/collaborations/${campaignId}/approve-licensing`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    submitPlatformCode: builder.mutation<
      any,
      { campaignId: string; milestoneId: string; platform: string; value: string | boolean }
    >({
      query: ({ campaignId, milestoneId, platform, value }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submit-platform-licensing`,
        method: 'POST',
        body: { platform, value },
      }),
      invalidatesTags: (_r, _e, { campaignId }) => [
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getSubmissionNotes: builder.query<
      { notes: SubmissionNote[] },
      { campaignId: string; submissionId: string }
    >({
      query: ({ campaignId, submissionId }) =>
        `/athlete/collaborations/${campaignId}/submissions/${submissionId}/notes`,
      providesTags: (_r, _e, { submissionId }) => [{ type: 'Notes', id: submissionId }],
    }),
    addSubmissionNote: builder.mutation<
      { notes: SubmissionNote[] },
      { campaignId: string; submissionId: string; text: string; parentId?: string | null }
    >({
      query: ({ campaignId, submissionId, text, parentId }) => ({
        url: `/athlete/collaborations/${campaignId}/submissions/${submissionId}/notes`,
        method: 'POST',
        body: { text, parentId: parentId ?? null, metadata: { target: 'general' } },
      }),
      invalidatesTags: (_r, _e, { submissionId }) => [{ type: 'Notes', id: submissionId }],
    }),
    resolveSubmissionNote: builder.mutation<
      { notes: SubmissionNote[] },
      { campaignId: string; submissionId: string; noteId: string; resolved: boolean }
    >({
      query: ({ campaignId, noteId, resolved }) => ({
        url: `/athlete/collaborations/${campaignId}/notes/${noteId}`,
        method: 'PATCH',
        body: { resolved },
      }),
      invalidatesTags: (_r, _e, { submissionId }) => [{ type: 'Notes', id: submissionId }],
    }),
    // ── The content calendar's organic posts ───────────────────────────────
    getPlannerWeek: builder.query<PlannerWeek, string>({
      query: (from) => `/athlete/planned-posts/week?from=${from}`,
      providesTags: ['PlannedPosts'],
    }),
    getPlannedPosts: builder.query<{ posts: PlannedPost[] }, { from: string; to: string }>({
      query: ({ from, to }) => `/athlete/planned-posts?from=${from}&to=${to}`,
      providesTags: ['PlannedPosts'],
    }),
    createPlannedPost: builder.mutation<PlannedPost, Record<string, unknown>>({
      query: (body) => ({ url: '/athlete/planned-posts', method: 'POST', body }),
      invalidatesTags: ['PlannedPosts'],
    }),
    updatePlannedPost: builder.mutation<PlannedPost, { id: string } & Record<string, unknown>>({
      query: ({ id, ...body }) => ({ url: `/athlete/planned-posts/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['PlannedPosts'],
    }),
    deletePlannedPost: builder.mutation<{ ok: true }, string>({
      query: (id) => ({ url: `/athlete/planned-posts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PlannedPosts'],
    }),
    // The content system: idea bank, the assistant, trends
    getIdeas: builder.query<{ ideas: PlannedPost[] }, void>({
      query: () => '/athlete/planned-posts/ideas',
      providesTags: ['PlannedPosts'],
    }),
    askContentAssistant: builder.mutation<
      { reply: string; pieces: ProposedPiece[]; mode: 'ai' | 'planner' },
      { messages: AssistantMessage[] }
    >({
      query: (body) => ({ url: '/athlete/content-assistant/chat', method: 'POST', body }),
    }),
    addProposedPieces: builder.mutation<{ posts: PlannedPost[] }, { pieces: ProposedPiece[] }>({
      query: (body) => ({ url: '/athlete/content-assistant/add', method: 'POST', body }),
      invalidatesTags: ['PlannedPosts'],
    }),
    getContentTrends: builder.query<{ trends: ContentTrend[] }, void>({
      query: () => '/athlete/content-assistant/trends',
    }),
    /** Posts a manager suggested, waiting for you to add or skip them */
    getPostSuggestions: builder.query<{ suggestions: PlannedPost[] }, void>({
      query: () => '/athlete/planned-posts/suggestions',
      providesTags: ['PlannedPosts'],
    }),
    respondToSuggestions: builder.mutation<
      { added: number; skipped: number },
      { accept: string[]; skip: string[] }
    >({
      query: (body) => ({
        url: '/athlete/planned-posts/suggestions/respond',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PlannedPosts'],
    }),
    // ── The strategy call with the Creator Lab (athletes at a Lab school) ──
    getLabCall: builder.query<LabCallState, void>({
      query: () => '/athlete/lab-call',
      providesTags: ['LabCall'],
    }),
    bookLabCall: builder.mutation<LabCall, { startsAt: string; phone?: string }>({
      query: (body) => ({ url: '/athlete/lab-call', method: 'POST', body }),
      invalidatesTags: ['LabCall'],
    }),
    cancelLabCall: builder.mutation<{ ok: true }, void>({
      query: () => ({ url: '/athlete/lab-call', method: 'DELETE' }),
      invalidatesTags: ['LabCall'],
    }),
  }),
})

export const {
  useGetPlannerWeekQuery,
  useGetPlannedPostsQuery,
  useCreatePlannedPostMutation,
  useUpdatePlannedPostMutation,
  useDeletePlannedPostMutation,
  useGetIdeasQuery,
  useAskContentAssistantMutation,
  useAddProposedPiecesMutation,
  useGetContentTrendsQuery,
  useGetPostSuggestionsQuery,
  useRespondToSuggestionsMutation,
  useGetMyMarketplaceProfileQuery,
  useGetOpportunitiesQuery,
  useGetOpportunityQuery,
  useApplyToOpportunityMutation,
  useGetCampaignsQuery,
  useSetCollaborationArchivedMutation,
  useGetCampaignDetailQuery,
  useAcceptCampaignMutation,
  useRejectCampaignMutation,
  useRestoreCampaignMutation,
  useUpdateMilestoneMetadataMutation,
  useSubmitMilestoneMutation,
  useSubmitMilestoneCheckinMutation,
  useCreateMilestoneDateProposalMutation,
  useCancelMilestoneDateProposalMutation,
  useDeleteMilestoneSubmissionMutation,
  useGetSubmissionTimelineQuery,
  useAddSubmissionCommentMutation,
  useCreateStandaloneCollaborationMutation,
  useInviteBrandMutation,
  useGetPendingInvitationsQuery,
  useGetCreativeIdeasQuery,
  useSendCaptionChatMutation,
  useGetVideoSuggestionsQuery,
  useSendCollaborationMessageMutation,
  useCounterOfferMutation,
  useQuoteRateMutation,
  useCompleteStepMutation,
  useApproveLicensingMutation,
  useSubmitPlatformCodeMutation,
  useGetSubmissionNotesQuery,
  useAddSubmissionNoteMutation,
  useResolveSubmissionNoteMutation,
  useUpdateCollaborationMutation,
  useUpdateDeliverablesMutation,
  useScanContractMutation,
  useGetCalendarEventsQuery,
  useGetMilestoneEventsQuery,
  useGetAcademyProgressQuery,
  useReportAcademyEventMutation,
  useGetCollaborationEventsQuery,
  useRsvpCollaborationEventMutation,
  useGetCollaborationCalendarEventsQuery,
  useGetLabCallQuery,
  useBookLabCallMutation,
  useCancelLabCallMutation,
} = collaborationsApi
